/**
 * ReportTemplateNetBody — GRC Report Template Configuration (NetBody).
 *
 * GRC-side master for configuring report templates (sections/structure).
 * Left panel: list of templates (table-design1).
 * Right panel: template detail — name, description, sections editor
 *   (add / remove / reorder sections).
 * Toolbar: "New Template" button.
 *
 * Data: reportTemplatesService from auditService.ts
 * Flow:  Layout1 (Route) → ReportTemplateView → ReportTemplateNetBody
 */
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, GripVertical, Trash2, ArrowUp, ArrowDown, Save, Search } from 'lucide-react';

import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import Skeleton from '../../components/common/Skeleton';
import NoDataFound from '../../components/common/NoDataFound';
import Toast, { ToastMessage } from '../../components/common/Toast';
import {
  reportTemplatesService,
  ReportTemplate,
  ReportTemplateSection,
} from '../../services/auditService';
import { useFieldValidation, FieldValidationApi } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';

// ---------------------------------------------------------------------------
// Section editor row
// ---------------------------------------------------------------------------

interface SectionRowProps {
  section: ReportTemplateSection;
  index: number;
  total: number;
  vh: FieldValidationApi;
  onChange: (idx: number, updated: ReportTemplateSection) => void;
  onRemove: (idx: number) => void;
  onMoveUp: (idx: number) => void;
  onMoveDown: (idx: number) => void;
  disabled: boolean;
}

const SectionRow: React.FC<SectionRowProps> = ({
  section, index, total, vh, onChange, onRemove, onMoveUp, onMoveDown, disabled,
}) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: 12,
      background: 'var(--white)',
      border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-card)',
      padding: '10px 12px',
      marginBottom: 8,
    }}
    data-testid={`section-row-${index}`}
  >
    {/* Order handle + number */}
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        color: 'var(--text-tertiary)',
      }}
      title="Section order"
    >
      <GripVertical size={14} />
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 22, height: 22, borderRadius: 9999,
          background: 'var(--brand-subtle)', color: 'var(--interactive-primary-bg)',
          fontSize: 12, fontWeight: 600,
        }}
      >
        {index + 1}
      </span>
    </div>

    {/* Labeled fields — Key (short) + Title (wide) */}
    <div
      style={{
        flex: 1, minWidth: 0,
        display: 'grid', gridTemplateColumns: 'minmax(120px, 200px) minmax(0, 1fr)', gap: 12,
      }}
    >
      <ValidatedInput
        vh={vh} name={`section-${index}-key`} code={109} required
        type="text"
        label="Section Key"
        placeholder="e.g. exec_summary"
        value={section.key}
        onChange={e => onChange(index, { ...section, key: e.target.value })}
        disabled={disabled}
        maxLength={100}
        noMargin
        className="min-w-0"
        data-testid={`section-key-${index}`}
      />
      <ValidatedTextarea
        vh={vh} name={`section-${index}-title`} code={109} required
        label="Section Title"
        placeholder="e.g. Executive Summary"
        value={section.title}
        onChange={e => onChange(index, { ...section, title: e.target.value })}
        disabled={disabled}
        maxLength={255}
        rows={2}
        className="min-w-0"
        data-testid={`section-title-${index}`}
      />
    </div>

    {/* Reorder + remove */}
    <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
      <button
        type="button"
        className="table-list-action"
        onClick={() => onMoveUp(index)}
        disabled={disabled || index === 0}
        aria-label="Move section up"
        data-tooltip="Move up"
        data-testid={`section-moveup-${index}`}
      >
        <ArrowUp size={15} />
      </button>
      <button
        type="button"
        className="table-list-action"
        onClick={() => onMoveDown(index)}
        disabled={disabled || index === total - 1}
        aria-label="Move section down"
        data-tooltip="Move down"
        data-testid={`section-movedown-${index}`}
      >
        <ArrowDown size={15} />
      </button>
      <button
        type="button"
        className="table-list-action table-list-action--delete"
        onClick={() => onRemove(index)}
        disabled={disabled}
        aria-label="Remove section"
        data-tooltip="Remove section"
        data-tooltip-pos="left"
        data-testid={`section-remove-${index}`}
      >
        <Trash2 size={15} />
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// New Template Modal
// ---------------------------------------------------------------------------

interface NewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (t: ReportTemplate) => void;
  onError: (message: string) => void;
  existingNames: string[];
}

const NewTemplateModal: React.FC<NewTemplateModalProps> = ({ isOpen, onClose, onCreated, onError, existingNames }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const vh = useFieldValidation();

  const reset = () => { setName(''); setDescription(''); vh.reset(); };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const ok = vh.validateAll([
      { name: 'name', code: 109, value: name, required: true },
      { name: 'description', code: 110, value: description },
    ]);
    if (!ok) return;
    // FE best-effort uniqueness pre-check (BE is the hard gate)
    if (!isUniqueLocal(939, name, existingNames.map(n => ({ name: n })))) {
      vh.setError('name', 'A template with this name already exists.');
      return;
    }
    setSaving(true);
    try {
      const t = await reportTemplatesService.create({
        name: name.trim(),
        description: description.trim() || undefined,
        sections: [],
      });
      onCreated(t);
      reset();
    } catch (err: any) {
      onError(err?.response?.data?.detail || err.message || 'Failed to create template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm" data-testid="new-template-modal">
      <Modal.Header>Create Report Template</Modal.Header>
      <Modal.Body>
        <form
          id="new-template-form"
          onSubmit={handleSubmit}
          noValidate
          className="flex flex-col gap-y-4"
        >
          <ValidatedInput
            vh={vh} name="name" code={109} required
            type="text"
            label="Template Name"
            placeholder="e.g. Standard Audit Report"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={255}
            data-testid="new-template-name"
          />
          <ValidatedTextarea
            vh={vh} name="description" code={110}
            label="Description (optional)"
            placeholder="Briefly describe this template's purpose"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            data-testid="new-template-description"
          />
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          form="new-template-form"
          variant="fill"
          icon={Save}
          disabled={!name.trim() || saving}
          data-testid="new-template-submit"
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Template Detail / Editor Panel
// ---------------------------------------------------------------------------

interface TemplateEditorPanelProps {
  template: ReportTemplate;
  onUpdated: (t: ReportTemplate) => void;
  onError: (message: string) => void;
  otherNames: string[];
}

const TemplateEditorPanel: React.FC<TemplateEditorPanelProps> = ({ template, onUpdated, onError, otherNames }) => {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [sections, setSections] = useState<ReportTemplateSection[]>(
    (template.sections ?? []).slice().sort((a, b) => a.order - b.order),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const vh = useFieldValidation();

  // Reset when template changes (different row selected)
  useEffect(() => {
    setName(template.name);
    setDescription(template.description ?? '');
    setSections((template.sections ?? []).slice().sort((a, b) => a.order - b.order));
    setDirty(false);
    setError(null);
    vh.reset();
  }, [template.id]);

  const markDirty = () => setDirty(true);

  const handleSectionChange = (idx: number, updated: ReportTemplateSection) => {
    setSections(prev => {
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
    markDirty();
  };

  const handleSectionRemove = (idx: number) => {
    setSections(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((s, i) => ({ ...s, order: i }));
    });
    markDirty();
  };

  const handleMoveUp = (idx: number) => {
    if (idx === 0) return;
    setSections(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((s, i) => ({ ...s, order: i }));
    });
    markDirty();
  };

  const handleMoveDown = (idx: number) => {
    setSections(prev => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next.map((s, i) => ({ ...s, order: i }));
    });
    markDirty();
  };

  const handleAddSection = () => {
    setSections(prev => [
      ...prev,
      { key: '', title: '', order: prev.length },
    ]);
    markDirty();
  };

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Template name is required.'); return; }

    // Validate sections: every key + title must be non-empty (clear, top-of-panel banner)
    for (let i = 0; i < sections.length; i++) {
      if (!sections[i].key.trim()) {
        setError(`Section ${i + 1}: key is required.`);
        return;
      }
      if (!sections[i].title.trim()) {
        setError(`Section ${i + 1}: title is required.`);
        return;
      }
    }

    // Validate unique keys
    const keys = sections.map(s => s.key.trim());
    const unique = new Set(keys);
    if (unique.size !== keys.length) {
      setError('Section keys must be unique within a template.');
      return;
    }

    // Field-level format validation (inline errors + a top banner so the block is never silent)
    const specs = [
      { name: 'name', code: 109, value: name, required: true },
      { name: 'description', code: 110, value: description },
    ];
    sections.forEach((s, i) => {
      specs.push({ name: `section-${i}-key`, code: 109, value: s.key, required: true });
      specs.push({ name: `section-${i}-title`, code: 109, value: s.title, required: true });
    });
    if (!vh.validateAll(specs)) {
      setError('Please fix the highlighted fields before updating.');
      return;
    }

    // FE best-effort uniqueness pre-check (BE is the hard gate)
    if (!isUniqueLocal(939, name, otherNames.map(n => ({ name: n })))) {
      vh.setError('name', 'A template with this name already exists.');
      setError('A template with this name already exists.');
      return;
    }

    setSaving(true);
    try {
      const updated = await reportTemplatesService.update(template.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        sections: sections.map((s, i) => ({ ...s, key: s.key.trim(), title: s.title.trim(), order: i })),
      });
      setDirty(false);
      onUpdated(updated);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to save template.';
      setError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="netbody-header">
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
          <FileText size={16} strokeWidth={1.75} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
            {name || 'Edit Template'}
          </span>
        </span>
        {dirty && (
          <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 9999, fontWeight: 500, flexShrink: 0 }}>
            Unsaved changes
          </span>
        )}
      </div>

      <div className="netbody-content" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '14px 16px' }}>
        {error && (
          <div className="alert alert-error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ValidatedInput
            vh={vh} name="name" code={109} required
            type="text"
            label="Template Name"
            value={name}
            onChange={e => { setName(e.target.value); markDirty(); }}
            maxLength={255}
            data-testid="edit-template-name"
          />

          <ValidatedTextarea
            vh={vh} name="description" code={110}
            label="Description (optional)"
            value={description}
            onChange={e => { setDescription(e.target.value); markDirty(); }}
            rows={3}
            data-testid="edit-template-description"
          />

          {/* Sections editor */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>
                Report Sections ({sections.length})
              </span>
              <Button
                variant="outline"
                icon={Plus}
                onClick={handleAddSection}
                disabled={saving}
                data-testid="add-section-btn"
              >
                Create
              </Button>
            </div>

            {sections.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>
                No sections yet. Click "Add Section" to define the report structure.
              </p>
            ) : (
              sections.map((sec, idx) => (
                <SectionRow
                  key={idx}
                  section={sec}
                  index={idx}
                  total={sections.length}
                  vh={vh}
                  onChange={handleSectionChange}
                  onRemove={handleSectionRemove}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  disabled={saving}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          borderTop: '1px solid var(--border-subtle)',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="update"
          icon={Save}
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="save-template-btn"
        >
          {saving ? 'Updating…' : 'Update'}
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Main ReportTemplateNetBody component
// ---------------------------------------------------------------------------

const ReportTemplateNetBody: React.FC = () => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [newOpen, setNewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const result = await reportTemplatesService.list({ page, limit: pageSize });
      setTemplates(result.items);
      setTotalRecords(result.total);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load templates.' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setPage(1); }, [pageSize]);

  const handleRowClick = async (t: ReportTemplate) => {
    setDetailLoading(true);
    try {
      const detail = await reportTemplatesService.get(t.id);
      setSelectedTemplate(detail);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load template.' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreated = (t: ReportTemplate) => {
    setNewOpen(false);
    setSelectedTemplate(t);
    refresh();
    setToast({ type: 'success', message: 'Template created.' });
  };

  const handleUpdated = (t: ReportTemplate) => {
    setSelectedTemplate(t);
    setTemplates(prev => prev.map(x => x.id === t.id ? { ...x, ...t } : x));
    setToast({ type: 'success', message: 'Template saved.' });
  };

  // Client-side filter of the loaded page by template name (the list endpoint
  // does not yet take a search param — filter the rows already in hand).
  const visibleTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div
      className="flex overflow-hidden"
      style={{ gap: 0, height: 'calc(100vh - 145px)' }}
      data-testid="report-template-master"
    >
              {/* ── Left nav card: template picker (fixed-zone) ────────── */}
              <div className="netbody-card side-nav-card side-nav-card--fixed">
                {/* FIXED top: heading + Create */}
                <div className="side-nav-card-heading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Reports</span>
                  <Button
                    variant="fill"
                    icon={Plus}
                    onClick={() => setNewOpen(true)}
                    data-testid="new-template-btn"
                  />
                </div>

                {/* FIXED top: search box (finds a template by name) */}
                <div className="side-nav-card-search">
                  <div className="relative">
                    <Search
                      size={13}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search templates…"
                      className="w-full text-xs rounded-md border border-slate-200 bg-white pl-7 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      data-testid="template-search"
                    />
                  </div>
                </div>

                {/* SCROLLS: the template list (only this region scrolls) */}
                <nav aria-label="Report templates">
                  {loading &&
                    // icon · name (width varies per row) · count chip — matches the real row.
                    [0, 1, 2, 3, 4, 5].map(i => {
                      const nameWidths = ['72%', '55%', '84%', '48%', '66%', '60%'];
                      return (
                        <div
                          key={i}
                          className="side-panel-menu-item"
                          style={{ pointerEvents: 'none' }}
                          aria-hidden="true"
                        >
                          <Skeleton.Circle size={14} />
                          <Skeleton.Box width={nameWidths[i]} height={11} style={{ flex: '0 1 auto' }} />
                          <Skeleton.Circle size={18} style={{ marginLeft: 'auto' }} />
                        </div>
                      );
                    })}

                  {!loading && visibleTemplates.length === 0 && (
                    <NoDataFound
                      title="No templates"
                      message={search.trim() ? 'No templates match your search.' : 'Click "Create" to add one.'}
                      icon={<FileText size={28} />}
                    />
                  )}

                  {!loading && visibleTemplates.map(t => {
                    const active = selectedTemplate?.id === t.id;
                    const count = (t.sections ?? []).length;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleRowClick(t)}
                        className={`side-panel-menu-item${active ? ' side-panel-menu-item--active' : ''}`}
                        data-testid={`template-row-${t.id}`}
                        title={t.name}
                      >
                        <FileText size={14} style={{ flexShrink: 0 }} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                          {t.name}
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 600,
                            minWidth: 20,
                            textAlign: 'center',
                            padding: '1px 6px',
                            borderRadius: 9999,
                            background: active ? 'var(--interactive-primary-bg)' : 'var(--border-subtle)',
                            color: active ? '#fff' : 'var(--text-tertiary)',
                          }}
                          title={`${count} section${count === 1 ? '' : 's'}`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </nav>

                {/* FIXED bottom: pagination */}
                {totalRecords > 0 && (
                  <div className="side-nav-card-footer">
                    <Pagination
                      page={page}
                      pageSize={pageSize}
                      totalRecords={totalRecords}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                      recordLabel="templates"
                    />
                  </div>
                )}
              </div>

              {/* ── Right content card: template editor (fixed heading, scroll-only body) ── */}
              <div className="netbody-card side-content-card side-content-card--fixed flex flex-col flex-1 min-w-0">
                {detailLoading && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                    <div style={{ fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>
                      Loading template…
                    </div>
                  </div>
                )}
                {!detailLoading && selectedTemplate && (
                  <TemplateEditorPanel
                    template={selectedTemplate}
                    onUpdated={handleUpdated}
                    onError={message => setToast({ type: 'error', message })}
                    otherNames={templates.filter(t => t.id !== selectedTemplate.id).map(t => t.name)}
                  />
                )}
                {!detailLoading && !selectedTemplate && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 84, height: 84, borderRadius: 9999, background: 'var(--brand-subtle)', color: 'var(--interactive-primary-bg)' }}>
                      <FileText size={38} strokeWidth={1.5} />
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Select a template to edit its sections</span>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Pick a report from the list on the left.</span>
                    </div>
                  </div>
                )}
              </div>

              <NewTemplateModal
                isOpen={newOpen}
                onClose={() => setNewOpen(false)}
                onCreated={handleCreated}
                onError={message => setToast({ type: 'error', message })}
                existingNames={templates.map(t => t.name)}
              />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ReportTemplateNetBody;
